<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, inject } from 'vue'

const __t = inject('__t')
const page = usePage()
const isRtl = computed(() => page.props.locale === 'ar')

const values = [
    {
        title: 'value_transparency',
        desc: 'value_transparency_desc',
        icon: 'mdi-hand-heart',
    },
    {
        title: 'value_quality',
        desc: 'value_quality_desc',
        icon: 'mdi-check-decagram',
    },
    {
        title: 'value_human_resources',
        desc: 'value_human_resources_desc',
        icon: 'mdi-account-group',
    },
    {
        title: 'value_partnership',
        desc: 'value_partnership_desc',
        icon: 'mdi-handshake',
    },
    {
        title: 'value_efficiency',
        desc: 'value_efficiency_desc',
        icon: 'mdi-chart-bell-curve-cumulative',
    },
    {
        title: 'value_innovation',
        desc: 'value_innovation_desc',
        icon: 'mdi-lightbulb-on-outline',
    },
    {
        title: 'value_social_responsibility',
        desc: 'value_social_responsibility_desc',
        icon: 'mdi-heart-multiple-outline',
    },
]
</script>

<template>
    <section class="relative overflow-hidden bg-primary py-20" :dir="isRtl ? 'rtl' : 'ltr'">
        <!-- Background Elements -->
        <div class="absolute inset-0 pointer-events-none opacity-40">
            <div class="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-900/50 rounded-full blur-[120px]">
            </div>
            <div class="absolute bottom-[-10%] left-[-10%] w-[60%] h-[60%] bg-blue-800/50 rounded-full blur-[120px]">
            </div>
            <!-- Decorative circles from Image 2 -->
            <div class="absolute top-1/4 left-0 w-96 h-96 border-[40px] border-white/5 rounded-full -translate-x-1/2">
            </div>
            <div
                class="absolute bottom-1/4 right-0 w-96 h-96 border-[40px] border-white/5 rounded-full translate-x-1/2">
            </div>
        </div>

        <div class="container mx-auto px-6 relative z-10">
            <!-- Header Section (Inspired by Image 1) -->
            <div class="flex flex-col lg:flex-row items-stretch mb-24 gap-12 lg:gap-20">
                <!-- Left: Image -->
                <div class="lg:w-1/2" data-aos="fade-right">
                    <div class="relative group h-full">
                        <!-- Top-left rounded corner as per design -->
                        <div
                            class="aspect-[4/3] lg:aspect-auto h-full rounded-tl-[100px] rounded-br-[40px] overflow-hidden shadow-2xl relative">
                            <img src="/assets/almatin/1-(2).png" alt="Almatin Factory"
                                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                        </div>
                        <!-- Border decoration -->
                        <div
                            class="absolute -bottom-4 -right-4 w-32 h-32 border-r-8 border-b-8 border-secondary rounded-br-[40px] pointer-events-none">
                        </div>
                    </div>
                </div>

                <!-- Right: Content -->
                <div class="lg:w-1/2 flex flex-col justify-center" data-aos="fade-left">
                    <div class="space-y-6 mb-12">
                        <div class="inline-block relative">
                            <span
                                class="bg-secondary text-primary px-4 py-1 text-2xl lg:text-3xl font-black relative z-10">
                                {{ __t('our_values_title') }}
                            </span>
                            <div class="absolute top-1 left-1 w-full h-full bg-secondary/30 -z-0"></div>
                        </div>
                        <h2 class="text-5xl lg:text-7xl font-black text-white leading-tight drop-shadow-lg">
                            {{ __t('our_success_title') }}
                        </h2>
                    </div>

                    <div
                        class="bg-white/95 backdrop-blur-md p-8 lg:p-12 rounded-bl-[80px] rounded-tr-[40px] shadow-2xl relative overflow-hidden group">
                        <!-- Decorative element -->
                        <div class="absolute top-0 right-0 w-24 h-1 bg-secondary"></div>
                        <p class="text-primary text-xl lg:text-2xl font-bold leading-relaxed relative z-10">
                            {{ __t('values_intro_text') }}
                        </p>
                        <!-- Background text/accent -->
                        <div
                            class="absolute -bottom-10 -right-10 text-[10rem] font-black text-gray-50/50 select-none pointer-events-none group-hover:text-gray-100 transition-colors duration-500">
                            07
                        </div>
                    </div>
                </div>
            </div>

            <!-- Values List (Inspired by Image 2 - Staggered layout for premium look) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
                <div v-for="(value, index) in values" :key="index"
                    class="group relative flex items-start gap-8 bg-white/5 backdrop-blur-sm p-8 rounded-[2rem] border border-white/10 transition-all duration-500 hover:bg-white/10 hover:border-secondary/30 hover:-translate-y-2 shadow-lg"
                    data-aos="fade-up" :data-aos-delay="index * 100">

                    <!-- Icon Container -->
                    <div class="shrink-0 relative">
                        <div
                            class="w-16 h-16 rounded-full bg-blue-600/30 border-2 border-white/20 flex items-center justify-center relative z-10 shadow-[0_0_20px_rgba(30,64,175,0.4)] group-hover:bg-secondary group-hover:border-white transition-all duration-500 overflow-hidden">
                            <v-icon size="32"
                                class="text-white group-hover:text-primary transition-colors duration-500">
                                {{ value.icon }}
                            </v-icon>
                        </div>
                        <!-- Inner glow -->
                        <div
                            class="absolute inset-0 bg-secondary rounded-full blur-xl scale-75 opacity-0 group-hover:opacity-30 transition-opacity duration-500">
                        </div>
                    </div>

                    <!-- Text Content -->
                    <div class="flex-1 space-y-3">
                        <h3
                            class="text-2xl lg:text-3xl font-black text-white group-hover:text-secondary transition-colors duration-300">
                            {{ __t(value.title) }}
                        </h3>
                        <p
                            class="text-gray-300 leading-relaxed text-lg lg:text-xl font-medium line-clamp-3 group-hover:line-clamp-none transition-all duration-500">
                            {{ __t(value.desc) }}
                        </p>
                    </div>

                    <!-- Corner accent -->
                    <div class="absolute top-0 right-0 w-12 h-12 overflow-hidden rounded-tr-[2rem]">
                        <div
                            class="absolute top-0 right-0 w-1 bg-secondary/0 group-hover:bg-secondary h-full transition-all duration-500">
                        </div>
                        <div
                            class="absolute top-0 right-0 h-1 bg-secondary/0 group-hover:bg-secondary w-full transition-all duration-500">
                        </div>
                    </div>
                </div>
            </div>


        </div>
    </section>
</template>

<style scoped>
/* Any custom styles to enhance the premium feel */
.container {
    max-width: 1400px;
}
</style>
